import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Header,
  Input,
  Label,
  Message,
  Modal,
  Segment,
  Table,
  TextArea
} from 'semantic-ui-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { API, showError, showSuccess } from '../../helpers';
import { TIME_DISCOUNT_OPTIONS, trimModelName } from '../../components/modelGridUtils';

const TYPE_OPTIONS = [
  { key: 'text', text: 'text', value: 'text' },
  { key: 'image', text: 'image', value: 'image' },
  { key: 'audio', text: 'audio', value: 'audio' },
  { key: 'embedding', text: 'embedding', value: 'embedding' },
  { key: 'video', text: 'video', value: 'video' }
];

export const WIRE_FORMAT_OPTIONS = [
  { key: 'openai',    text: 'OpenAI',    value: 'openai' },
  { key: 'anthropic', text: 'Anthropic', value: 'anthropic' },
  { key: 'ali-video', text: '万象视频',  value: 'ali-video' },
  { key: 'ali-image', text: '万象图像',  value: 'ali-image' },
  { key: 'sd-video', text: 'Seedance',  value: 'sd-video' },
  { key: 'sd-image', text: 'Seedream',  value: 'sd-image' },
  { key: 'minimax-video', text: 'MiniMax 视频', value: 'minimax-video' },
  { key: 'sd-audio', text: 'Seed 音频', value: 'sd-audio' },
  { key: 'minimax-audio', text: 'MiniMax 语音', value: 'minimax-audio' },
];

const emptyForm = {
  group: 'default',
  model: '',
  orig_name: '',
  upstream_name: '',
  type: 'text',
  wire_format: 'openai',
  tags: '',
  model_ratio: 1.0,
  completion_ratio: 1.0,
  cache_ratio: 0,
  cache_creation_ratio: 0,
  with_video_ratio: 1.0,
  sd_discount: 1.0,
  priority: 0,
  display_order: 0,
  enabled: true,
  resolution_ratios: '',
  description: '',
  tier_threshold: 0,
  tier_model_ratio: 0,
  tier_completion_ratio: 0,
  tier_cache_ratio: 0,
  tier_cache_creation_ratio: 0,
  time_discount_code: ''
};

const ChannelAbilities = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const channelName = searchParams.get('name');
  const [abilities, setAbilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupOptions, setGroupOptions] = useState([]);
  const [addForm, setAddForm] = useState(emptyForm);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // non-null = edit mode
  const [copyTarget, setCopyTarget] = useState(null); // ability to copy
  const [copyGroup, setCopyGroup] = useState('');
  const [copyModel, setCopyModel] = useState('');
  const [copyMultiplier, setCopyMultiplier] = useState('1');
  // 默认只看已启用的能力，禁用的行通过开关显示
  const [showDisabled, setShowDisabled] = useState(false);

  const rowKey = (a) => `${a.group}|${a.model}`;

  // Insert-or-replace by (group, model): the backend upserts on the
  // (group, model, channel_id) PK, so appending blindly would render two rows
  // with the same key while only one row actually exists server-side.
  const upsertAbility = (list, ability) =>
    list.some((a) => rowKey(a) === rowKey(ability))
      ? list.map((a) => (rowKey(a) === rowKey(ability) ? ability : a))
      : [...list, ability];

  const startCopy = (ability) => {
    setCopyTarget(ability);
    setCopyGroup(trimModelName(ability.group));
    setCopyModel(trimModelName(ability.model));
    setCopyMultiplier('1');
  };

  // The per-token rates get scaled. Text billing sums them additively
  // (quota = prompt×model_ratio + completion×completion_ratio + cached×cache_ratio
  // + cacheCreation×cache_creation_ratio), so completion_ratio is a price in its
  // own right, not a multiple of model_ratio, and has to be scaled explicitly.
  //
  // Zero cache ratios are "derive from model_ratio" sentinels — 0 × k stays 0, and
  // the derived value picks up the already-scaled model_ratio, so leaving them is
  // correct.
  //
  // Left alone: sd_discount (an upstream discount, not our price), plus
  // resolution_ratios and with_video_ratio, which multiply model_ratio rather than
  // standing on their own — scaling those would square the factor.
  const SCALED_RATIO_FIELDS = [
    'model_ratio',
    'completion_ratio',
    'cache_ratio',
    'cache_creation_ratio',
    'tier_model_ratio',
    'tier_completion_ratio',
    'tier_cache_ratio',
    'tier_cache_creation_ratio'
  ];

  const scaledValue = (ability, field, factor) => {
    const v = parseFloat(ability[field]);
    if (isNaN(v)) return null;
    return v * factor;
  };

  const scaleRatios = (ability, factor) => {
    if (factor === 1) return ability;
    const scaled = { ...ability };
    SCALED_RATIO_FIELDS.forEach((f) => {
      const v = scaledValue(ability, f, factor);
      if (v !== null) scaled[f] = v;
    });
    return scaled;
  };

  const copyConflict = (() => {
    if (!copyTarget || !copyGroup || !copyModel.trim()) return null;
    const target = { group: copyGroup, model: copyModel.trim() };
    if (rowKey(target) === rowKey(copyTarget)) {
      return '目标分组与模型都未改变，请至少修改其中一项';
    }
    if (abilities.some((a) => rowKey(a) === rowKey(target))) {
      return `${target.group} / ${target.model} 已存在，复制将覆盖该行`;
    }
    return null;
  })();

  const handleCopy = async () => {
    if (!copyGroup) {
      showError('请选择目标分组');
      return;
    }
    const model = copyModel.trim();
    if (!model) {
      showError('请填写目标模型名称');
      return;
    }
    if (copyGroup === copyTarget.group && model === copyTarget.model) {
      showError('目标分组与模型都未改变');
      return;
    }
    const factor = parseFloat(copyMultiplier);
    if (isNaN(factor) || factor <= 0) {
      showError('倍数必须是大于 0 的数字');
      return;
    }
    const source = scaleRatios(copyTarget, factor);
    const payload = {
      ...source,
      group: trimModelName(copyGroup),
      model,
      orig_name: trimModelName(source.orig_name),
      upstream_name: trimModelName(source.upstream_name),
    };
    const res = await API.post(`/api/channel/${id}/abilities`, payload);
    const { success, message, data } = res.data;
    if (success) {
      showSuccess('已复制');
      setAbilities((prev) => upsertAbility(prev, data));
      setCopyTarget(null);
    } else {
      showError(message);
    }
  };

  const load = async () => {
    setLoading(true);
    const res = await API.get(`/api/channel/${id}/abilities`);
    const { success, message, data } = res.data;
    if (success) {
      setAbilities(data || []);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const fetchGroups = async () => {
    try {
      const res = await API.get('/api/group/');
      setGroupOptions(
        res.data.data.map((g) => ({ key: g, text: g, value: g }))
      );
    } catch (e) {
      showError(e.message);
    }
  };

  useEffect(() => {
    load();
    fetchGroups();
  }, [id]);

  const validateResolutionRatios = (val) => {
    if (!val || val.trim() === '') return true;
    try { JSON.parse(val); return true; } catch { return false; }
  };

  const openAddModal = () => {
    setEditTarget(null);
    setAddForm(emptyForm);
    setShowAddModal(true);
  };

  const openEditModal = (ability) => {
    setEditTarget(ability);
    setAddForm({
      ...ability,
      orig_name: trimModelName(ability.orig_name),
      upstream_name: trimModelName(ability.upstream_name),
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditTarget(null);
    setAddForm(emptyForm);
  };

  const handleSave = async () => {
    if (!addForm.model.trim()) {
      showError('请填写模型名称');
      return;
    }
    if (!validateResolutionRatios(addForm.resolution_ratios)) {
      showError('Resolution Ratios 不是合法的 JSON');
      return;
    }
    const payload = {
      ...addForm,
      model: editTarget ? addForm.model : trimModelName(addForm.model),
      orig_name: trimModelName(addForm.orig_name),
      upstream_name: trimModelName(addForm.upstream_name),
    };
    if (editTarget) {
      const res = await API.put(`/api/channel/${id}/abilities`, payload);
      const { success, message, data } = res.data;
      if (success) {
        showSuccess('已更新');
        setAbilities((prev) =>
          prev.map((a) => (rowKey(a) === rowKey(editTarget) ? data : a))
        );
        closeModal();
      } else {
        showError(message);
      }
    } else {
      const model = payload.model;
      if (abilities.some((a) => rowKey(a) === rowKey(payload))) {
        showError(`${addForm.group} / ${model} 已存在，请直接编辑该行`);
        return;
      }
      const res = await API.post(`/api/channel/${id}/abilities`, payload);
      const { success, message, data } = res.data;
      if (success) {
        showSuccess('已添加');
        setAbilities((prev) => upsertAbility(prev, data));
        closeModal();
      } else {
        showError(message);
      }
    }
  };

  const disabledCount = abilities.filter((a) => !a.enabled).length;
  const visibleAbilities = showDisabled ? abilities : abilities.filter((a) => a.enabled);

  return (
    <Segment loading={loading}>
      <Header as='h3'>
        渠道 {channelName ? `${channelName} (${id})` : `#${id}`} — 能力管理
        <Button size='small' as={Link} to='/channel' style={{ marginLeft: 16 }}>
          返回渠道列表
        </Button>
        <Button size='small' positive onClick={openAddModal} style={{ marginLeft: 8 }}>
          添加能力
        </Button>
        <Checkbox
          toggle
          label={`显示已禁用 (${disabledCount})`}
          checked={showDisabled}
          onChange={(_, { checked }) => setShowDisabled(checked)}
          style={{ marginLeft: 16, fontSize: '0.8rem', fontWeight: 'normal', verticalAlign: 'middle' }}
        />
      </Header>

      <Modal open={showAddModal} onClose={closeModal} size='large'>
        <Modal.Header>{editTarget ? '编辑能力行' : '添加能力行'}</Modal.Header>
        <Modal.Content scrolling>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>分组</label>
                {editTarget ? (
                  <Input value={addForm.group} readOnly />
                ) : (
                <Dropdown
                  selection
                  allowAdditions
                  options={groupOptions}
                  value={addForm.group}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, group: value }))
                  }
                />
                )}
              </Form.Field>
              <Form.Field>
                <label>模型</label>
                <Input
                  value={addForm.model}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, model: value }))
                  }
                  placeholder='模型名称'
                  readOnly={!!editTarget}
                />
              </Form.Field>
              <Form.Field>
                <label>计费名</label>
                <Input
                  value={addForm.orig_name}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, orig_name: value }))
                  }
                  placeholder='留空则按模型名计费'
                />
              </Form.Field>
              <Form.Field>
                <label>上游模型名</label>
                <Input
                  value={addForm.upstream_name}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, upstream_name: value }))
                  }
                  placeholder='留空则透传模型名'
                />
              </Form.Field>
              <Form.Field>
                <label>Tags</label>
                <Input
                  value={addForm.tags}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, tags: value }))
                  }
                  placeholder='标签(逗号分隔)'
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>类型</label>
                <Dropdown
                  selection
                  options={TYPE_OPTIONS}
                  value={addForm.type}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, type: value }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Wire Format</label>
                <Dropdown
                  selection
                  options={WIRE_FORMAT_OPTIONS}
                  value={addForm.wire_format}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, wire_format: value }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>ModelRatio</label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.model_ratio}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, model_ratio: parseFloat(value) || 0 }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>CompletionRatio</label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.completion_ratio}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({
                      ...p,
                      completion_ratio: parseFloat(value) || 0
                    }))
                  }
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>CacheRatio <span style={{ color: '#888', fontWeight: 'normal' }}>(0 = 按 ModelRatio×0.1)</span></label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.cache_ratio}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, cache_ratio: parseFloat(value) || 0 }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>CacheCreationRatio <span style={{ color: '#888', fontWeight: 'normal' }}>(0 = 按 ModelRatio×1.25)</span></label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.cache_creation_ratio}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, cache_creation_ratio: parseFloat(value) || 0 }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>WithVideoRatio</label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.with_video_ratio}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, with_video_ratio: parseFloat(value) || 0 }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>SdDiscount <span style={{ color: '#888', fontWeight: 'normal' }}>(Seedance 折扣, 1 = 原价)</span></label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.sd_discount}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, sd_discount: parseFloat(value) || 0 }))
                  }
                  placeholder='如 0.4'
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>TierThreshold <span style={{ color: '#888', fontWeight: 'normal' }}>(0 = 不分层)</span></label>
                <Input
                  type='number'
                  value={addForm.tier_threshold}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, tier_threshold: parseInt(value) || 0 }))
                  }
                  placeholder='如 32000'
                />
              </Form.Field>
              <Form.Field>
                <label>TierModelRatio</label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.tier_model_ratio}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, tier_model_ratio: parseFloat(value) || 0 }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>TierCompletionRatio</label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.tier_completion_ratio}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, tier_completion_ratio: parseFloat(value) || 0 }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>TierCacheRatio <span style={{ color: '#888', fontWeight: 'normal' }}>(0 = 不变)</span></label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.tier_cache_ratio}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, tier_cache_ratio: parseFloat(value) || 0 }))
                  }
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>TierCacheCreationRatio <span style={{ color: '#888', fontWeight: 'normal' }}>(0 = 不变)</span></label>
                <Input
                  type='number'
                  step='0.01'
                  value={addForm.tier_cache_creation_ratio}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, tier_cache_creation_ratio: parseFloat(value) || 0 }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>错峰计费 <span style={{ color: '#888', fontWeight: 'normal' }}>(仅文本计费生效；本行单独生效)</span></label>
                <Dropdown
                  selection
                  options={TIME_DISCOUNT_OPTIONS}
                  value={addForm.time_discount_code || ''}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, time_discount_code: value }))
                  }
                />
              </Form.Field>
              <Form.Field />
              <Form.Field />
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>Priority</label>
                <Input
                  type='number'
                  value={addForm.priority}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, priority: parseInt(value) || 0 }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>DisplayOrder</label>
                <Input
                  type='number'
                  value={addForm.display_order}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, display_order: parseInt(value) || 0 }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Enabled</label>
                <Checkbox
                  checked={addForm.enabled}
                  onChange={(_, { checked }) =>
                    setAddForm((p) => ({ ...p, enabled: checked }))
                  }
                  style={{ marginTop: 8 }}
                />
              </Form.Field>
              <Form.Field />
            </Form.Group>
            {addForm.type === 'video' && (
              <Form.Field>
                <label>Resolution Ratios (JSON, 仅 video 类型)</label>
                <TextArea
                  value={addForm.resolution_ratios}
                  onChange={(_, { value }) =>
                    setAddForm((p) => ({ ...p, resolution_ratios: value }))
                  }
                  placeholder='{"720P": 0.9, "1080P": 1.6}'
                  rows={2}
                />
              </Form.Field>
            )}
            <Form.Field>
              <label>Description</label>
              <TextArea
                value={addForm.description}
                onChange={(_, { value }) =>
                  setAddForm((p) => ({ ...p, description: value }))
                }
                placeholder='说明文字，支持 HTML（如 <a href="...">链接</a>）'
                rows={3}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeModal}>取消</Button>
          <Button positive onClick={handleSave}>{editTarget ? '保存' : '添加'}</Button>
        </Modal.Actions>
      </Modal>

      {/* Abilities table */}
      <div style={{ overflowX: 'auto' }}>
      <Table basic compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>分组</Table.HeaderCell>
            <Table.HeaderCell>模型</Table.HeaderCell>
            <Table.HeaderCell>类型</Table.HeaderCell>
            <Table.HeaderCell>Wire Format</Table.HeaderCell>
            <Table.HeaderCell>Priority</Table.HeaderCell>
            <Table.HeaderCell>DisplayOrder</Table.HeaderCell>
            <Table.HeaderCell>错峰计费</Table.HeaderCell>
            <Table.HeaderCell>Enabled</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {visibleAbilities.map((a) => {
            const key = rowKey(a);
            return (
              <Table.Row key={key} style={!a.enabled ? { opacity: 0.45, background: 'rgba(0,0,0,0.03)' } : undefined}>
                <Table.Cell>{a.group}</Table.Cell>
                <Table.Cell>
                  {trimModelName(a.model)}
                  {trimModelName(a.upstream_name) ? (
                    <div style={{ fontSize: '0.85em', color: 'rgba(0,0,0,.5)' }}>
                      → {trimModelName(a.upstream_name)}
                    </div>
                  ) : null}
                </Table.Cell>
                <Table.Cell>{a.type}</Table.Cell>
                <Table.Cell>{WIRE_FORMAT_OPTIONS.find(o => o.value === a.wire_format)?.text || a.wire_format}</Table.Cell>
                <Table.Cell>{a.priority}</Table.Cell>
                <Table.Cell>{a.display_order}</Table.Cell>
                <Table.Cell>
                  {a.time_discount_code ? (
                    <Label color='teal' size='tiny'>{a.time_discount_code}</Label>
                  ) : (
                    '-'
                  )}
                </Table.Cell>
                <Table.Cell><Label color={a.enabled ? 'green' : 'red'} size='tiny'>{a.enabled ? '是' : '否'}</Label></Table.Cell>
                <Table.Cell>
                  <Button size='small' onClick={() => openEditModal(a)}>
                    编辑
                  </Button>
                  <Button size='small' color='blue' onClick={() => startCopy(a)}>
                    复制
                  </Button>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      </div>

      <Modal open={!!copyTarget} onClose={() => setCopyTarget(null)} size='tiny'>
        <Modal.Header>复制能力</Modal.Header>
        <Modal.Content>
          <p>
            以 <strong>{trimModelName(copyTarget?.group)}</strong> / <strong>{trimModelName(copyTarget?.model)}</strong> 的配置为模板，
            复制到下面的分组与模型（可只改其中一项，例如模型升级 opus-4.6 → opus-4.8）：
          </p>
          <Form>
            <Form.Field>
              <label>目标分组</label>
              <Dropdown
                selection
                allowAdditions
                search
                placeholder='选择目标分组'
                options={groupOptions}
                value={copyGroup}
                onChange={(_, { value }) => setCopyGroup(value)}
                style={{ width: '100%' }}
              />
            </Form.Field>
            <Form.Field>
              <label>目标模型</label>
              <Input
                value={copyModel}
                onChange={(_, { value }) => setCopyModel(value)}
                placeholder='模型名称'
              />
            </Form.Field>
            <Form.Field>
              <label>
                价格倍数{' '}
                <span style={{ color: '#888', fontWeight: 'normal' }}>
                  (1 = 原价。缩放 ModelRatio、CompletionRatio 与缓存/分层价；
                  SdDiscount、WithVideoRatio、ResolutionRatios 不变)
                </span>
              </label>
              <Input
                type='number'
                step='0.01'
                min='0'
                value={copyMultiplier}
                onChange={(_, { value }) => setCopyMultiplier(value)}
                placeholder='1'
              />
            </Form.Field>
          </Form>
          {(() => {
            const factor = parseFloat(copyMultiplier);
            if (!copyTarget || isNaN(factor) || factor === 1) return null;
            if (factor <= 0) {
              return (
                <Message negative size='small' style={{ marginTop: 12 }}>
                  倍数必须大于 0
                </Message>
              );
            }
            // A 0 ratio is a "derive from model_ratio" sentinel, not a price —
            // listing it as 0 → 0 is just noise.
            const rows = SCALED_RATIO_FIELDS.map((f) => {
              const to = scaledValue(copyTarget, f, factor);
              if (to === null || to === 0) return null;
              const from = to / factor;
              return { f, from, to };
            }).filter(Boolean);
            if (rows.length === 0) return null;
            return (
              <Message info size='small' style={{ marginTop: 12 }}>
                <Message.Header>缩放后的价格</Message.Header>
                <Message.List>
                  {rows.map(({ f, from, to }) => (
                    <Message.Item key={f}>
                      {f}: {Number(from.toFixed(6))} → {Number(to.toFixed(6))}
                    </Message.Item>
                  ))}
                </Message.List>
              </Message>
            );
          })()}
          {copyConflict && (
            <Message warning size='small' style={{ marginTop: 12 }}>
              {copyConflict}
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setCopyTarget(null)}>取消</Button>
          <Button positive onClick={handleCopy}>复制</Button>
        </Modal.Actions>
      </Modal>

    </Segment>
  );
};

export default ChannelAbilities;
